import { useDispatch } from 'react-redux'
import { trackInternalEvent } from '@rio/tracking'
import { useI18n } from 'builder/hooks/useI18n'
import { useTypedSelector } from 'builder/hooks/useTypedSelector'
import { actions, selectors } from 'builder/modules/resumeEditor'
import { FetchStatuses } from 'builder/modules/constants'
import { ButtonTheme } from 'builder/components/Button'
import { useTailoredDesignation } from 'builder/hooks/useTailoredDesignation'
import { useAutoTailoring } from 'builder/components/AutoTailoring/hooks/useAutoTailoring'
import { useMediaQueries } from 'builder/hooks/useMediaQueries'
import { EditorMobileFooter } from 'builder/components/PreviewPanel'
import { useResponsiveButtonSize } from 'builder/hooks/useResponsiveButtonSize'
import { Caption, JobPostingContainer, Title } from '../../styles'
import { JobPostingDetails } from '../../components/JobPostingDetails/JobPostingDetails'
import { JobPostingProps } from '../../types'
import {
  Container,
  Content,
  ImproveScoreContainer,
  ImproveScoreBadge,
  ButtonsContainer,
  StyledButton,
  ArrowTargetIcon,
  ImproveScoreBadgeContainer,
} from './styles'

export const ConfirmTailoringStep = (props: JobPostingProps) => {
  const { children, canScrollBottom } = props
  const { i18n } = useI18n()
  const {
    saveJobPostingDataStatus,
    jobPostingLinkForModal: jobPostingLink,
    resumeScoreAPIData: { averageScore, suggestedJobTitle, employerName },
  } = useTypedSelector(selectors.optimizerSection)
  const resume = useTypedSelector(selectors.resume)
  const isSavingJobPostingData = saveJobPostingDataStatus === FetchStatuses.loading
  const tailoredDesignation = useTailoredDesignation(suggestedJobTitle, employerName)
  const { tryToStartAutoTailoring, isAutoTailoringEnabled } = useAutoTailoring()
  const dispatch = useDispatch()
  const requiredScore = 100 - averageScore
  const { isTablet } = useMediaQueries()
  const buttonSize = useResponsiveButtonSize()

  const titleText = () => {
    if (averageScore < 30) {
      return i18n.t('builder.resume_optimizer.job_posting.poor_match.title')
    } else if (averageScore > 70) {
      return i18n.t('builder.resume_optimizer.job_posting.good_match.title')
    } else {
      return i18n.t('builder.resume_optimizer.job_posting.normal_match.title')
    }
  }

  const handleTryAgain = () => {
    dispatch(actions.resetResumeOptimizerProcess())
    trackInternalEvent('click_try_again_resume_optimizer_result_screen')
  }

  const handleStartManualTailoring = () => {
    dispatch(
      actions.startImprovingResumeForEditorWithTabs({
        jobPostingLink,
        resumeId: resume?.id,
      }),
    )
    if (isAutoTailoringEnabled) {
      trackInternalEvent('click_edit_manually_resume_optimizer_result_screen')
    } else {
      trackInternalEvent('click_start_improving_resume_optimizer_result_screen')
    }
  }

  const handleStartAutoTailoring = () => {
    trackInternalEvent('click_auto_tailor_resume_optimizer_result_screen')
    tryToStartAutoTailoring()
  }

  const buttons = (
    <ButtonsContainer>
      <StyledButton
        theme={isAutoTailoringEnabled ? 'ghost' : 'primary'}
        onClick={handleStartManualTailoring}
        isDisabled={isSavingJobPostingData}
        size={buttonSize}
      >
        {isAutoTailoringEnabled
          ? i18n.t('builder.resume_optimizer.job_posting.manually_button')
          : i18n.t('builder.resume_optimizer.job_posting.start_improving')}
      </StyledButton>
      {isAutoTailoringEnabled && (
        <StyledButton
          theme={ButtonTheme.alternative}
          onClick={handleStartAutoTailoring}
          isLoading={isSavingJobPostingData}
          isDisabled={isSavingJobPostingData}
          leadingIcon={<ArrowTargetIcon />}
          size={buttonSize}
        >
          {i18n.t('builder.resume_optimizer.job_posting.auto_tailor_button')}
        </StyledButton>
      )}
    </ButtonsContainer>
  )

  return (
    <>
      <JobPostingContainer>
        <Container>
          <JobPostingDetails
            title={tailoredDesignation}
            score={averageScore}
            onChangeLink={handleTryAgain}
          />
          <Content>
            <Title>{titleText()}</Title>
            <ImproveScoreContainer>
              <ImproveScoreBadgeContainer>
                <ImproveScoreBadge>+{requiredScore}%</ImproveScoreBadge>
              </ImproveScoreBadgeContainer>
              <Caption>{i18n.t('builder.resume_optimizer.job_posting.poor_match.content')}</Caption>
            </ImproveScoreContainer>
          </Content>
          {!isTablet && buttons}
        </Container>
      </JobPostingContainer>
      {children}
      {isTablet && (
        <EditorMobileFooter isFooterShadowVisible={canScrollBottom}>{buttons}</EditorMobileFooter>
      )}
    </>
  )
}
